import { transient, autoinject, bindable, computedFrom } from "aurelia-framework";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { CoachmarkClientService } from "../../../services/coachmark/coachmark-client.service";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { Reflection } from "../../../utilities/reflection";

@autoinject
@transient()
export class Coachmark {

    private onCoachmarkTriggered = 'onCoachmarkTriggered';
    private onCoachmarkClosed = 'onCoachmarkClosed';

    private subscriptions: Subscription[] = [];

    private _element: Element;

    @bindable
    public step: number;

    @bindable
    public nextStep: number;

    @bindable
    public group: string;

    @bindable
    public position: 'bottom' | 'left' | 'right' | 'top' | 'bottom-left' = 'bottom';

    @bindable
    public selector: string;

    @bindable
    public zindex: string;

    @bindable
    public showOverlay: boolean = true;

    public isVisible: boolean = false;

    @computedFrom(Reflection.getPropertyPath<Coachmark>(z => z.group), Reflection.getPropertyPath<Coachmark>(z => z.step))
    public get key(): string {
        return `${this.group}_${this.step}`;
    }

    @computedFrom(Reflection.getPropertyPath<Coachmark>(z => z.group), Reflection.getPropertyPath<Coachmark>(z => z.nextStep))
    public get nextKey(): string {
        return `${this.group}_${this.nextStep}`;
    }

    checkElementisVisibleTimeout = null;
    private checkElementisVisible()
    {
        this.checkElementisVisibleTimeout = setTimeout(() => {
            this.isVisible = this.isInViewport(this.anchoredElement);
            this.checkElementisVisible();
        }, 1000);
    }

    private isInViewport(elem:Element) {
        if(!elem) return false;
        const bounding = elem.getBoundingClientRect();
        return (
            bounding.width > 0 &&
            bounding.height > 0 &&
            bounding.top >= 0 &&
            bounding.left >= 0 &&
            bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    @computedFrom(Reflection.getPropertyPath<Coachmark>(z => z._element), Reflection.getPropertyPath<Coachmark>(z => z.selector), 
    Reflection.getPropertyPath<Coachmark>(z => z._element.isConnected))
    public get anchoredElement(): Element {

        if (!this._element || (this._element && !this._element.isConnected)) {
            const elements = document.querySelectorAll(this.selector);

            if (elements.length > 1) {
                console.warn(`There are multiple (${elements.length}) elements matching this selector '${this.selector}'`);
            }

            this._element = elements[0];
        }
        return this._element;
    }

    @computedFrom(Reflection.getPropertyPath<Coachmark>(z => z.anchoredElement), Reflection.getPropertyPath<Coachmark>(z => z.position))
    public get leftPx() {
        if (!this.anchoredElement) {
            return null;
        }

        let correction = 0;

        if (this.position == 'right') {
            correction = this.anchoredElement.clientWidth;
        }

        return this.anchoredElement.getBoundingClientRect().left + correction;
    }

    @computedFrom(Reflection.getPropertyPath<Coachmark>(z => z.anchoredElement),
        Reflection.getPropertyPath<Coachmark>(z => z.anchoredElement.clientWidth),
        Reflection.getPropertyPath<Coachmark>(z => z.position))
    public get rightPx() {

        if (!this.anchoredElement) {
            return null;
        }

        let correction = 0;

        if (this.position == 'left') {
            correction = this.anchoredElement.clientWidth;
        }

        return this.anchoredElement.getBoundingClientRect().right + correction;
    }

    @computedFrom(Reflection.getPropertyPath<Coachmark>(z => z.anchoredElement),
        Reflection.getPropertyPath<Coachmark>(z => z.anchoredElement.clientHeight),
        Reflection.getPropertyPath<Coachmark>(z => z.position))
    public get topPx() {
        if (!this.anchoredElement) {
            return null;
        }

        let correction = 0;

        if (this.position == 'bottom' || this.position == 'bottom-left') {
            correction = this.anchoredElement.clientHeight;
        }

        return this.anchoredElement.getBoundingClientRect().top + correction;
    }

    constructor(private eventAggregator: EventAggregator, private coachmarkClientService: CoachmarkClientService,
        private cacheClientService: CacheClientService) { }

    checkIfComplete(key: string) {
        this.coachmarkClientService.isCoachmarkCompleted(key).then(isCompleted => {
            if (!isCompleted) {
                this.isVisible = true;
                this.checkElementisVisible();
            }
            else {
                this.goNext();
            }
        });
    }

    public completeCoachmark = () => {
        this.coachmarkClientService.setCoachmarkCompleteRequest(this.key);
        this.goNext();
    }

    private goNext() {
        clearTimeout(this.checkElementisVisibleTimeout);
        this.isVisible = false;
        if (this.nextStep) {
            this.eventAggregator.publish(this.onCoachmarkTriggered, this.nextKey);
        }
    }

    attached() {
        this.subscriptions.push(this.eventAggregator.subscribe(this.onCoachmarkTriggered, aggregatorData => {
            if (aggregatorData == this.key) {
                this.checkIfComplete(this.key);
            }
        }));

        this.subscriptions.push(this.eventAggregator.subscribe(this.onCoachmarkClosed, () => {
            clearTimeout(this.checkElementisVisibleTimeout);
            this.isVisible = false;
        }));

        this.cacheClientService.get<string>('coachmark-group', CacheLocation.SessionStorage).then(group => {
            if (group) {
                if (`${group}_1` == this.key) {
                    this.checkIfComplete(this.key);
                }
            }
        });
    }

    detached() {
        this.subscriptions.forEach(x => x.dispose());
    }
}